<script lang="ts">
  import { page } from "$app/state";
  import { authStore as Current } from "$lib/stores";
  import { goto } from "$app/navigation";
  import { cn } from "$lib/utility";
  import { routes } from "$lib/routes";
  import logoDark from "$lib/images/logo/logo_dark_mono.svg";
  import { Sidebar } from "$lib/components/ui";
  import Button from "$lib/components/landing/redesigned/components/Button.svelte";
  import { ChevronDown } from "lucide-svelte";
  import { slide } from "svelte/transition";
  import { SVG } from "$lib/components";
  import { hamburgerMenuIcon } from "$lib/images/icons";

  let sidebar = Sidebar.useSidebar();

  // Props with defaults
  let {
    className = "",
    variant = "sidebar",
    collapsible = "offcanvas",
    theme = "dark",
  } = $props<{
    className?: string;
    variant?: "sidebar" | "floating" | "inset";
    collapsible?: "icon" | "none" | "offcanvas";
    theme?: "light" | "dark";
  }>();

  // State management
  let currentPath = $state(page.url.pathname);
  let featuresDropdownOpen = $state(false);
  let companyDropdownOpen = $state(false);
  let useCasesDropdownOpen = $state(false);

  // Effect to update current path
  $effect(() => {
    currentPath = page.url.pathname;
  });

  function handleSidebarNavigation() {
    if (sidebar.isMobile) {
      sidebar.toggle();
    }
  }

  function handleNavigation(e: MouseEvent | KeyboardEvent, route: string) {
    e.preventDefault();
    handleSidebarNavigation();
    goto(route);
  }

  function toggleDropdown(dropdown: "features" | "company" | "useCases") {
    if (dropdown === "features") {
      featuresDropdownOpen = !featuresDropdownOpen;
      companyDropdownOpen = false;
      useCasesDropdownOpen = false;
    } else if (dropdown === "company") {
      companyDropdownOpen = !companyDropdownOpen;
      featuresDropdownOpen = false;
      useCasesDropdownOpen = false;
    } else {
      useCasesDropdownOpen = !useCasesDropdownOpen;
      featuresDropdownOpen = false;
      companyDropdownOpen = false;
    }
  }

  // Computed classes
  let wrapperClasses = $derived(
    cn("bg-black text-white flex flex-col flex-shrink-0 flex-grow-0", "w-full h-screen overflow-hidden", className),
  );
</script>

<Sidebar.Root {collapsible} {variant}>
  <Sidebar.Content class="sidebar-content lg:hidden" style="z-index: 10001 !important;">
    <aside class={wrapperClasses} style="z-index: 10001 !important;">
      <!-- Header -->
      <Sidebar.Header class="p-0">
        <div class="flex h-14 items-center justify-between border-b border-white/10 px-4 py-4">
          <div class="flex items-center gap-2.5">
            <button
              type="button"
              class="inline-flex items-center justify-center rounded-base p-2 focus:outline-none"
              aria-controls="mobile-menu"
              aria-expanded={sidebar?.open ? "true" : "false"}
              onclick={() => {
                sidebar.toggle();
              }}
            >
              <span class="sr-only">Close menu</span>
              <SVG file={hamburgerMenuIcon} className={cn("max-w-9 w-full max-h-9 text-white")} />
            </button>
            <a href="/" class="flex items-center">
              <img src={logoDark} alt="Logo" class="h-5" />
            </a>
          </div>
          <button
            class="rounded-md p-2 text-white/60 hover:text-white hover:bg-white/10 transition-colors"
            onclick={() => sidebar.toggle()}
            aria-label="Close menu"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>
      </Sidebar.Header>

      <!-- Navigation Content -->
      <div class="flex-1 overflow-y-auto p-6">
        <nav class="space-y-2">
          <!-- Features Section -->
          <div>
            <button
              class="flex w-full items-center justify-between rounded-lg px-4 py-3 text-left text-white hover:bg-white/5 transition-colors"
              onclick={() => toggleDropdown("features")}
            >
              <span class="text-lg font-medium">Features</span>
              <ChevronDown
                class={cn(
                  "h-5 w-5 text-white/60 transition-transform duration-200",
                  featuresDropdownOpen && "rotate-180",
                )}
              />
            </button>
            {#if featuresDropdownOpen}
              <div class="mt-1 space-y-1 pl-4" transition:slide={{ duration: 200 }}>
                <a
                  href="/features/ai-web-scraping"
                  class="block rounded-lg px-4 py-3 text-white hover:bg-white/5 transition-colors"
                  onclick={(e) => handleNavigation(e, "/features/ai-web-scraping")}
                >
                  <div class="text-base">AI Web Scraping</div>
                  <div class="text-sm text-white/50 mt-0.5">Gather data from websites automatically</div>
                </a>

                <a
                  href="/features/ai-pdf-processing"
                  class="block rounded-lg px-4 py-3 text-white hover:bg-white/5 transition-colors"
                  onclick={(e) => handleNavigation(e, "/features/ai-pdf-processing")}
                >
                  <div class="text-base">AI PDF Processing</div>
                  <div class="text-sm text-white/50 mt-0.5">Extract insights from PDF documents</div>
                </a>
              </div>
            {/if}
          </div>

          <!-- Use Cases -->
          <div>
            <button
              class="flex w-full items-center justify-between rounded-lg px-4 py-3 text-left text-white hover:bg-white/5 transition-colors"
              onclick={() => toggleDropdown("useCases")}
            >
              <span class="text-lg font-medium">Use Cases</span>
              <ChevronDown
                class={cn(
                  "h-5 w-5 text-white/60 transition-transform duration-200",
                  useCasesDropdownOpen && "rotate-180",
                )}
              />
            </button>
            {#if useCasesDropdownOpen}
              <div class="mt-1 space-y-1 pl-4" transition:slide={{ duration: 200 }}>
                <a
                  href="/use-cases/ai-ecommerce-automation"
                  class="block rounded-lg px-4 py-3 text-white hover:bg-white/5 transition-colors"
                  onclick={(e) => handleNavigation(e, "/use-cases/ai-ecommerce-automation")}
                >
                  <div class="text-base">E-commerce Competitor Research</div>
                  <div class="text-sm text-white/50 mt-0.5">Analyze and track competitor strategies</div>
                </a>

                <a
                  href="/use-cases/recruiting-ai-resume-screening"
                  class="block rounded-lg px-4 py-3 text-white hover:bg-white/5 transition-colors"
                  onclick={(e) => handleNavigation(e, "/use-cases/recruiting-ai-resume-screening")}
                >
                  <div class="text-base">AI Resume Screening</div>
                  <div class="text-sm text-white/50 mt-0.5">Review Resumes 100X Faster with AI</div>
                </a>
              </div>
            {/if}
          </div>

          <!-- Integrations -->
          <a
            href="/integrations"
            class={cn(
              "block rounded-lg px-4 py-3 text-lg font-medium text-white hover:bg-white/5 transition-colors",
              currentPath === "/integrations" && "bg-white/10",
            )}
            onclick={(e) => handleNavigation(e, "/integrations")}
          >
            Integrations
          </a>

          <!-- Pricing -->
          <a
            href={routes.pricing}
            class={cn(
              "block rounded-lg px-4 py-3 text-lg font-medium text-white hover:bg-white/5 transition-colors",
              currentPath === routes.pricing && "bg-white/10",
            )}
            onclick={(e) => handleNavigation(e, routes.pricing)}
          >
            Pricing
          </a>

          <!-- Company Section -->
          <div>
            <button
              class="flex w-full items-center justify-between rounded-lg px-4 py-3 text-left text-white hover:bg-white/5 transition-colors"
              onclick={() => toggleDropdown("company")}
            >
              <span class="text-lg font-medium">Company</span>
              <ChevronDown
                class={cn(
                  "h-5 w-5 text-white/60 transition-transform duration-200",
                  companyDropdownOpen && "rotate-180",
                )}
              />
            </button>
            {#if companyDropdownOpen}
              <div class="mt-1 space-y-1 pl-4" transition:slide={{ duration: 200 }}>
                <a
                  href="/about-us"
                  class="block rounded-lg px-4 py-3 text-white hover:bg-white/5 transition-colors"
                  onclick={(e) => handleNavigation(e, "/about-us")}
                >
                  <div class="text-base">About</div>
                  <div class="text-sm text-white/50 mt-0.5">Learn about our mission and team</div>
                </a>

                <a
                  href={routes.blog}
                  class="block rounded-lg px-4 py-3 text-white hover:bg-white/5 transition-colors"
                  onclick={(e) => handleNavigation(e, routes.blog)}
                >
                  <div class="text-base">Blog</div>
                  <div class="text-sm text-white/50 mt-0.5">Latest news and insights</div>
                </a>
              </div>
            {/if}
          </div>
        </nav>
      </div>

      <!-- Auth Buttons -->
      {#if !$Current.isLoggedIn}
        <div class="border-t border-white/10 p-6 space-y-3">
          <button
            type="button"
            class="w-full flex items-center justify-center gap-3 rounded-full px-4 py-3 border border-white/10 bg-gradient-to-b from-[#0C0A09] to-[#080706] text-white hover:from-[#161311] hover:to-[#0F0D0B] shadow-[inset_0px_1px_0px_0px_rgba(255,255,255,0.08),inset_0px_3px_2px_0px_rgba(255,255,255,0.02)] before:absolute before:inset-0 before:rounded-full before:bg-gradient-to-b before:from-white/5 before:to-transparent before:pointer-events-none transition-all"
            onclick={() => {
              Current.initiateGoogleSignin();
              sidebar.toggle();
            }}
          >
            <span class="text-base font-medium">Google Login</span>
            <svg
              class="h-5 w-5"
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 48 48"
              xmlns:xlink="http://www.w3.org/1999/xlink"
              style="display: block;"
            >
              <path
                fill="#EA4335"
                d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
              ></path>
              <path
                fill="#4285F4"
                d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
              ></path>
              <path
                fill="#FBBC05"
                d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
              ></path>
              <path
                fill="#34A853"
                d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
              ></path>
              <path fill="none" d="M0 0h48v48H0z"></path>
            </svg>
          </button>
          <Button
            onclick={() => {
              goto(routes.signUp);
              sidebar.toggle();
            }}
            variant="primary"
            size="md"
            icon="send-wrapped"
            fullWidth={true}
            iconPosition="right"
            className="h-12"
          >
            Start For Free
          </Button>
        </div>
      {/if}
    </aside>
  </Sidebar.Content>
</Sidebar.Root>

<!-- Overlay for mobile -->
{#if sidebar.open && sidebar.isMobile}
  <div class="fixed inset-0 bg-black bg-opacity-50 lg:hidden" style="z-index: 10000 !important;">
    <button
      type="button"
      class="fixed inset-0 bg-transparent"
      onclick={() => sidebar.toggle()}
      aria-label="Close sidebar overlay"
    ></button>
  </div>
{/if}

<style>
  /* Override Sheet component z-index when used as sidebar */
  :global([data-sidebar="sidebar"][data-mobile="true"]) {
    z-index: 10000 !important;
  }

  /* Override the sheet overlay z-index */
  :global([data-state="open"] ~ [data-sidebar="sidebar"][data-mobile="true"]) {
    z-index: 10000 !important;
  }

  /* Target the sheet portal container */
  :global([data-portal]) {
    z-index: 9999 !important;
  }

  /* Remove the greyish overlay background - make it fully black */
  :global(.data-\[state\=open\]\:animate-in.bg-black\/80) {
    background-color: rgba(0, 0, 0, 0.5) !important;
  }
</style>
